import React, { Fragment, useRef, useState, useEffect, useCallback } from 'react'

import '../index.scss'
import { useDispatch, useSelector } from 'react-redux'

import {
  CameraOutlined,
  LoadingOutlined,
  PushpinFilled,
  PushpinOutlined,
} from '@ant-design/icons'
import ClassNames from 'classnames'
import moment from 'moment'
import ReactPlayer from 'react-player'
import DurationComponent from '../Duration'
import CenterControlIcon from '../icons/center_ovif.svg'
import ControlIconOff from '../icons/control_camera_off.svg'
import ControlIconOn from '../icons/control_camera_on.svg'
import DownloadIcon from '../icons/download.svg'
import FullscreenIcon from '../icons/fullscreen.svg'
import ExitFullscreenIcon from '../icons/fullscreen_exit.svg'
import StreamIcon from '../icons/live_stream.svg'
import MoveIconOff from '../icons/move_camera_off.svg'
import MoveIconOn from '../icons/move_camera_on.svg'
import PauseIcon from '../icons/pause.svg'
import PlayArrowIcon from '../icons/play_arrow.svg'
import ResetIcon from '../icons/refresh.svg'
import ReplayIcon from '../icons/replay.svg'

import ArrowLeftOffIcon from '../icons/arrow_back_off.svg'
import ArrowLeftOnIcon from '../icons/arrow_back_on.svg'

import ArrowRightOffIcon from '../icons/arrow_forward_off.svg'
import ArrowRightOnIcon from '../icons/arrow_forward_on.svg'

import ArrowTopOffIcon from '../icons/arrow_upward_off.svg'
import ArrowTopOnIcon from '../icons/arrow_upward_on.svg'

import ArrowBottomOffIcon from '../icons/arrow_downward_off.svg'
import ArrowBottomOnIcon from '../icons/arrow_downward_on.svg'

import ZoomInOffIcon from '../icons/zoom_in_off.svg'
import ZoomInOnIcon from '../icons/zoom_in_on.svg'

import ZoomOutOffIcon from '../icons/zoom_out_off.svg'
import ZoomOutOnIcon from '../icons/zoom_out_on.svg'

import { setPinnedCamerasMode } from '../../../redux/actions'
import SpinnerComponent from '../Spinner'
import usePanAndZoomVideo from '../usePanAndZoomVideo'

const speeds = [
  {
    value: 0.1,
    title: '0.1x',
  },
  {
    value: 0.5,
    title: '0.5x',
  },
  {
    value: 1,
    title: '1x',
  },
  {
    value: 2,
    title: '2x',
  },
  {
    value: 4,
    title: '4x',
  },
  {
    value: 8,
    title: '8x',
  },
  {
    value: 16,
    title: '16x',
  },
];

const StreamOptions = {
  webrtc: 'Ultra Fast',
  hls: 'Fast',
};

const MilestonePlayerComponent = ({
  tabActive,
  type,
  src,
  name,
  sourceObject,
  poster,
  loading,
  status,
  onvif,
  onRotate,
  onZoom,
  fullScreenCallBack,
  restartCallBack,
  restartLoadingCallBack,
  setOnPlay,
  zoomStateProps,
  isFullscreenProps,
  permission,
  serial,
  checkTransferZoomState,
  zoomStatePlayer,
  setZoomStatePlayer,
  restartQualityCallBack,
  logContent,
  logSerial,
  handleReportActionLog,
  dateTime,
  seekTo,
  protocol,
  isPlaybackSmallMode,
}) => {
  const progress = useRef(null)
  const refVideo = useRef(null)
  const refVideoTotal = useRef(null)
  const refInterval = useRef(null)
  const dispatch = useDispatch()
  const { pinnedCamerasMode, dataUser } = useSelector((state) => state)
  const [count, setCount] = useState(1)
  const [rotatable, setRotatable] = useState(false)
  const [rotate, setRotate] = useState()
  const [zooming, setZooming] = useState()
  const [pin, setPin] = useState([])
  const [isPinAll, setIsPinAll] = useState(false)
  const canvasRef = useRef(null)
  const [dimensions, setDimensions] = useState({})
  const [videoKey, setVideoKey] = useState()
  const [playedSecs, setPlayedSecs] = useState(0)
  const [key, setKey] = useState(Math.floor(Math.random() * 1000))
  const [modeLive, setModeLive] = useState()
  const [countHls, setCountHls] = useState(0)
  const [video, setVideo] = useState({
    controls: false,
    src: null,
    type: 'video',
    loading: true,
    playbackRate: 1.0,
    url: null,
    playing: false,
    volume: 0.5,
    muted: false,
    played: 0,
    duration: 0,
    ended: false,
    seeking: false,
    fullscreen: false,
  })

  const [videoError, setVideoError] = useState()
  const [zoomStateOption, setZoomStateOption] = useState()
  const [streamOptions, setStreamOptions] = useState([])

  const {
    containerRef,
    onMouseDown,
    onWheel,
    onReset,
    translateX,
    translateY,
    scale,
  } = usePanAndZoomVideo({ zoomable: video.controls })

  const [containerHeight, setContainerHeight] = useState('100%')

  let context
  if (canvasRef.current) {
    context = canvasRef.current.getContext('2d')
  }

  function getVideoSizeData(videoRef) {
    const ratio = videoRef.current.videoWidth / videoRef.current.videoHeight;
    const w = videoRef.current.videoWidth;
    const h = parseInt(w / ratio, 10);
    return {
      ratio,
      w,
      h,
    };
  }

  useEffect(() => {
    if (containerRef?.current?.clientWidth) {
      setContainerHeight(
        `${((containerRef.current.clientWidth * 9) / 16).toFixed(4)}px`
      );
    }
  }, [containerRef]);

  useEffect(() => {
    if (type === 'streaming') {
      let refCurrentTime;
      if (!videoKey) {
        refCurrentTime = setInterval(() => {
          setVideoKey(Math.random());
        }, 1000);
      }
      return () => {
        clearInterval(refCurrentTime);
      };
    }
  }, [type, videoKey]);

  useEffect(() => {
    if (protocol) {
      const options = protocol.split(';');

      setStreamOptions(options.map(item => StreamOptions[item]));
    }
  }, [protocol]);

  useEffect(() => {
    if (streamOptions && streamOptions.length === 1) {
      setModeLive(streamOptions[0]);
    }
  }, [streamOptions, zoomStatePlayer]);

  useEffect(() => {
    // Add listener when the video is actually available for
    // the browser to be able to check the dimensions of the video.
    if (refVideo?.current) {
      refVideo.current.addEventListener('loadedmetadata', function () {
        if (refVideo?.current) {
          const { w, h } = getVideoSizeData(refVideo);
          if (canvasRef?.current) {
            canvasRef.current.width = w;
            canvasRef.current.height = h;
          }
          setDimensions({ w, h });
        }
      });
    }
  }, []);

  const onDownloadDirectClick = () => {
    if (context && refVideo?.current) {
      context.fillRect(0, 0, dimensions.w, dimensions.h);
      context.drawImage(refVideo.current, 0, 0, dimensions.w, dimensions.h);

      const url = canvasRef.current.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `${serial ?? logSerial}_${dateTime ?? moment().format('DD-MM-YYYY')
        }.png`;
      link.href = url;
      link.click();
    }
  };

  useEffect(() => {
    setRotatable(type === 'streaming' && onvif);
  }, [onvif, type]);

  useEffect(() => {
    setPin(sessionStorage.getItem('pinCamera')?.split(',') || []);
    const pinCameraStorage = localStorage.getItem('pin_camera');
    if (pinCameraStorage) {
      setIsPinAll(JSON.parse(pinCameraStorage));
    }
  }, []);

  const onResetQualityStream = useCallback(
    dataObject => {
      setVideo(state => ({
        ...state,
        src: null,
        loading: true,
        playing: false,
        url: null,
      }));
      setPlayedSecs(0);
      setCountHls(0);
      restartQualityCallBack({ qualityLevel: dataObject?.qualityLevel });
    },
    [restartQualityCallBack]
  );

  useEffect(() => {
    if (zoomStateProps && zoomStatePlayer) {
      if (isFullscreenProps) {
        setZoomStatePlayer({
          dataZoomStates: [...zoomStateProps?.dataZoomStates],
          zoomState: zoomStateProps?.dataZoomStates?.[0]?.zoomState,
          resolution: zoomStateProps?.dataZoomStates?.[0]?.resolution,
        });
        if (
          zoomStateProps?.dataZoomStates?.[0]?.zoomState !==
          zoomStatePlayer.zoomState
        ) {
          // onResetQualityStream({
          //   qualityLevel: zoomStateProps?.dataZoomStates?.[0]?.zoomState,
          // });
        }
      } else {
        setZoomStatePlayer(zoomStateProps);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullscreenProps]);

  useEffect(() => {
    let refClear;
    if (rotate) {
      refClear = setTimeout(() => {
        setRotate(undefined);
      }, 500);
    }
    return () => {
      clearInterval(refClear);
    };
  }, [rotate]);

  useEffect(() => {
    let refClear;
    if (zooming) {
      refClear = setTimeout(() => {
        setZooming(undefined);
      }, 500);
    }
    return () => {
      clearInterval(refClear);
    };
  }, [zooming]);

  const onResetStream = useCallback(
    dataObject => {
      setVideo(state => ({
        ...state,
        src: null,
        loading: true,
        playing: false,
        url: null,
      }));
      setPlayedSecs(0);
      setCountHls(0);
      restartCallBack({ qualityLevel: dataObject?.qualityLevel });
    },
    [restartCallBack]
  );

  const onResetStreamLoading = useCallback(
    dataObject => {
      setVideo(state => ({
        ...state,
        src: null,
        loading: true,
        playing: false,
        url: null,
      }));
      setPlayedSecs(0);
      setCountHls(0);
      restartLoadingCallBack({ qualityLevel: dataObject?.qualityLevel });
    },
    [restartLoadingCallBack]
  );

  useEffect(() => {
    let refRestart;
    if (type === 'streaming' && video.playing) {
      let current = refVideo.current.currentTime + 20;
      refRestart = setInterval(() => {
        if (
          Math.round(Math.abs(current - refVideo.current.currentTime)) === 20
        ) {
          if (count <= 5) {
            console.log('countPlayer: ', count);
            setCount(count + 1);
            onResetStream();
          } else {
            setOnPlay(false);
          }
        } else {
          current = refVideo.current.currentTime + 20;
        }
      }, 10000);
    }
    return () => {
      clearInterval(refRestart);
    };
  }, [count, onResetStream, setOnPlay, type, video.playing]);

  useEffect(() => {
    if (seekTo > 0 && refVideo.current?.duration > 0) {
      refVideo.current.currentTime = seekTo;
      setVideo(state => ({
        ...state,
        played: parseFloat(seekTo / refVideo.current.duration),
      }));
    }
  }, [seekTo]);

  const handlePause = useCallback(() => {
    refVideo.current.pause();
    return setVideo(state => ({ ...state, playing: false }));
  }, []);

  useEffect(() => {
    if (type === 'video' && src) {
      let VOLUME = 0.5;
      refVideo.current.addEventListener('error', () => {
        setVideoError("Can't play this video");
        handlePause();
      });
      refVideo.current.setAttribute('src', src);
      refVideo.current.volume = VOLUME;
      refVideo.current.play();

      setVideo(state => ({
        ...state,
        src,
        type,
        volume: VOLUME,
        loading: false,
        playing: true,
      }));
    } else if (type === 'streaming' && sourceObject) {
      // console.log('🚀 ~ MilestonePlayerComponent ~ sourceObject:', sourceObject)
      let VOLUME = 0.5;
      if (!sourceObject?.link) {
        if ('srcObject' in refVideo.current) {
          refVideo.current.srcObject = sourceObject;
        } else {
          // Avoid using this in new browsers, as it is going away.
          refVideo.current.src = URL.createObjectURL(sourceObject);
        }
      }
      setVideo(state => ({
        ...state,
        src: sourceObject,
        type,
        loading: false,
        volume: VOLUME,
        playing: true,
      }));
    }
  }, [handlePause, sourceObject, src, type]);

  useEffect(() => {
    setVideo(state => ({
      ...state,
      loading,
    }));
  }, [loading]);

  useEffect(() => {
    if (refVideo?.current?.src) {
      handleDropDownTime();
    }
    return () => clearInterval(refInterval.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refVideo?.current?.src]);

  useEffect(() => {
    if (!zoomStatePlayer) return
    if (zoomStatePlayer?.dataZoomStates?.length) {
      const dataTemp = [];
      // eslint-disable-next-line no-unused-expressions
      zoomStatePlayer?.dataZoomStates?.map(item => {
        if (
          !dataTemp?.find(
            item1 =>
              item1?.resolution === item?.resolution &&
              item1?.zoomState === item?.zoomState
          )
        ) {
          dataTemp.push(item);
        }
      });
      setZoomStateOption(dataTemp);
    }
  }, [zoomStatePlayer]);

  const handleDropDownTime = () => {
    refInterval.current = setInterval(() => {
      const videoTime = refVideoTotal.current?.currentTimeTotal
        ? refVideoTotal.current?.currentTimeTotal +
        (refVideo.current.currentTime - refVideoTotal.current?.currentTime)
        : refVideo.current.currentTime;
      return setVideo(state => ({
        ...state,
        played: parseFloat(videoTime / refVideo.current.duration),
      }));
    }, 1000 / video.playbackRate);
  };

  const handlePlay = () => {
    refVideo.current.play();
    return setVideo(state => ({ ...state, playing: true }));
  };
  const handleRePlay = () => {
    refVideo.current.load();
    refVideo.current.src = video.src;
    refVideo.current.play();
    handleDropDownTime();
    return setVideo(state => ({
      ...state,
      playing: true,
      ended: false,
    }));
  };



  const handleEnded = () => {
    clearInterval(refInterval.current);
    return setVideo(state => ({ ...state, ended: true, playing: false }));
  };

  const handleDuration = () => {
    let duration = refVideo.current.duration;
    return setVideo(state => ({
      ...state,
      duration,
    }));
  };

  const handleSeekMouseDown = event => {
    return setVideo(state => ({
      ...state,
      seeking: true,
    }));
  };

  const handleSeekMouseUp = event => {
    setVideo(state => ({
      ...state,
      seeking: false,
    }));
    const jumpTime =
      (event.nativeEvent.offsetX / progress.current.offsetWidth) *
      video.duration;
    refVideo.current.currentTime = jumpTime;
  };

  const handleSeekChange = event => {
    event.persist();
    return setVideo(state => ({
      ...state,
      played: parseFloat(
        event.nativeEvent.offsetX / progress.current.offsetWidth
      ),
    }));
  };

  const handleSpeedChange = playbackRate => {
    refVideo.current.playbackRate = playbackRate;
    return setVideo(state => ({
      ...state,
      playbackRate,
    }));
  };
  const handleControlsChange = () => {
    onReset();
    return setVideo(state => ({
      ...state,
      controls: !state.controls,
    }));
  };
  const handleFullscreen = () => {
    // If fullscreen mode is active...
    if (
      !!(
        document.fullScreen ||
        document.webkitIsFullScreen ||
        document.mozFullScreen ||
        document.msFullscreenElement ||
        document.fullscreenElement
      )
    ) {
      // ...exit fullscreen mode
      // (Note: this can only be called on document)
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
      else if (document.webkitCancelFullScreen)
        document.webkitCancelFullScreen();
      else if (document.msExitFullscreen) document.msExitFullscreen();
    } else {
      if (containerRef.current.requestFullscreen)
        containerRef.current.requestFullscreen();
      else if (containerRef.current.mozRequestFullScreen)
        containerRef.current.mozRequestFullScreen();
      else if (containerRef.current.webkitRequestFullScreen) {
        refVideo.current.webkitRequestFullScreen();
      } else if (containerRef.current.msRequestFullscreen)
        containerRef.current.msRequestFullscreen();

      // Set zoom state
      if (type === 'streaming') fullScreenCallBack(true);

      setVideo(state => ({
        ...state,
        fullscreen: true,
      }));
    }
  };
  const exitHandler = useCallback(() => {
    if (
      !document.fullscreenElement &&
      !document.webkitIsFullScreen &&
      !document.mozFullScreen &&
      !document.msFullscreenElement
    ) {
      // Reset zoom
      onReset();
      // Set zoom state
      if (type === 'streaming') {
        fullScreenCallBack(false);
        // onResetQualityStream();
      }

      setVideo(state => ({
        ...state,
        fullscreen: false,
        controls: false,
      }));
    }
  }, [fullScreenCallBack, onReset, type]);

  useEffect(() => {
    document.addEventListener('fullscreenchange', exitHandler);
    document.addEventListener('webkitfullscreenchange', exitHandler);
    document.addEventListener('mozfullscreenchange', exitHandler);
    document.addEventListener('MSFullscreenChange', exitHandler);

    return () => {
      document.removeEventListener('fullscreenchange', exitHandler);
      document.removeEventListener('webkitfullscreenchange', exitHandler);
      document.removeEventListener('mozfullscreenchange', exitHandler);
      document.removeEventListener('MSFullscreenChange', exitHandler);
    };
  }, [exitHandler]);

  const handleRotate = useCallback(
    navigator => {
      if (rotatable && !rotate) {
        setRotate(navigator);
        return onRotate(navigator);
      }
    },
    [onRotate, rotatable, rotate]
  );

  const handleZoom = useCallback(
    zoom => {
      if (rotatable && !rotate) {
        setZooming(zoom);
        return onZoom(zoom);
      }
    },
    [onZoom, rotatable, rotate]
  );

  const handlePin = () => {
    const data = sessionStorage.getItem('pinCamera')?.split(',') || [];
    const dataPin = [...data, serial];
    sessionStorage.setItem('pinCamera', dataPin?.join());
    setPin(dataPin);
  };

  const handleUnPin = () => {
    const data =
      sessionStorage
        .getItem('pinCamera')
        ?.split(',')
        ?.filter(item => item !== serial) || [];
    sessionStorage.setItem('pinCamera', [...data]?.join());
    setPin([...data]);
  };

  const handleChangeMode = mode => {
    const data = pinnedCamerasMode;
    const dataTemp =
      data && JSON.parse(data)
        ? JSON.parse(data)?.filter(item => item?.serial !== serial)
        : [];
    const dataTempStorage = JSON.stringify([
      ...dataTemp,
      {
        serial,
        mode,
      },
    ]);
    dispatch(setPinnedCamerasMode(dataTempStorage));
    setModeLive(mode);
    // onResetQualityStream({
    //   qualityLevel: zoomStatePlayer?.zoomState,
    // });
  };

  const handleError = () => {
    if (countHls < 3) {
      setCountHls(countHls + 1);
      setVideo(state => ({
        ...state,
        loading: true,
      }));
      setPlayedSecs(0);
      setTimeout(() => {
        setVideo(state => ({
          ...state,
          loading: false,
        }));
      }, 2000);
    }
  };

  const handleProgress = playedSeconds => {
    if (playedSeconds - playedSecs >= 2) {
      setPlayedSecs(playedSecs + 1);
    } else {
      setPlayedSecs(playedSeconds);
    }
  };

  return (
    <div className="player-component" style={{ height: '100%' }}>
      <figure
        ref={containerRef}
        onMouseDown={onMouseDown}
        onWheel={onWheel}
        style={{
          paddingBottom: isPlaybackSmallMode ? '0px' : '56.25%',
          backgroundImage:
            video?.controls || playedSecs > 0
              ? 'unset'
              : `url(${poster})`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
        }}
      >
        <video
          id={`video-live-stream-${key}`}
          onDoubleClick={() => handleFullscreen()}
          poster={poster}
          ref={refVideo}
          className="player-video"
          // onDurationChange={handleDuration}
          // onEnded={handleEnded}
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
          }}
          autoPlay
        />

        {(video && video.loading) || checkTransferZoomState ? (
          type === 'streaming' ? (
            <>
              {type === 'streaming' && !isPlaybackSmallMode && (
                <div
                  className={
                    pin?.find((item) => item === serial) || isPinAll
                      ? 'top-player-component no-select pinCam'
                      : 'top-player-component no-select'
                  }
                >
                  <div className="stream_name">
                    <div className="status">{name}</div>
                    {pin?.find((item) => item === serial) || isPinAll ? (
                      <PushpinFilled
                        className="pin"
                        onClick={() => handleUnPin()}
                      />
                    ) : (
                      <PushpinOutlined
                        className="pin"
                        onClick={() => handlePin()}
                      />
                    )}
                  </div>
                  {status && status !== 'live' && (
                    <div className="stream_state">
                      <div className="status">{status}</div>
                    </div>
                  )}
                </div>
              )}
              <div className="bottom-player-component no-select">
                <div className="bottom-player-component no-select">
                  {
                    tabActive === 'live' && (
                      <div>
                        <button
                          className="play"
                          style={{ padding: '8px 6px 1px 6px' }}
                        >
                          <LoadingOutlined style={{ fontSize: '18px' }} />
                        </button>
                      </div>
                    )
                  }
                  <Fragment>
                    {tabActive === 'live' && (
                      <>
                        <button className="stream">
                          <img src={StreamIcon} alt="" />
                        </button>

                        {!isPlaybackSmallMode && (
                          <div>
                            <button
                              onClick={() => {
                                onResetStreamLoading({
                                  qualityLevel: zoomStatePlayer?.zoomState,
                                })
                              }}
                              className="reset-stream"
                            >
                              <img src={ResetIcon} alt="" />
                            </button>
                          </div>
                        )}
                      </>
                    )}
                    <button className="time">
                      <DurationComponent seconds={0} />
                    </button>
                    {tabActive === 'live' && <div className="stream-progress" />}
                    {!isPlaybackSmallMode && tabActive === 'Live' && (
                      <>
                        <div className="speed">
                          <button className="resolution">
                            {`${zoomStatePlayer?.resolution || 720}p` ?? ''}
                          </button>
                          <div className="speeds">
                            {zoomStateOption?.map((q, index) => (
                              <Fragment key={`${q?.zoomState}_${index}`}>
                                <button
                                  className={ClassNames({
                                    selected:
                                      q?.zoomState ===
                                      zoomStatePlayer?.zoomState,
                                  })}
                                  key={q?.zoomState}
                                  onClick={() => {
                                    setZoomStatePlayer({
                                      dataZoomStates:
                                        zoomStatePlayer?.dataZoomStates
                                          ? [...zoomStatePlayer?.dataZoomStates]
                                          : [],
                                      resolution: q?.resolution,
                                      zoomState: q?.zoomState,
                                    })
                                    onResetStreamLoading({
                                      qualityLevel: q?.zoomState,
                                    })
                                  }}
                                >
                                  {`${q?.resolution || 720}p` ?? ''}
                                </button>
                                <br />
                              </Fragment>
                            ))}
                          </div>
                        </div>
                        <div className="speed">
                          <button
                            className="resolution"
                            style={{
                              width:
                                modeLive === 'Fast' || '70px'
                            }}
                          >
                            {modeLive ?? 'Ultra Fast'}
                          </button>
                          <div className="speeds" style={{ width: '82px' }}>
                            {streamOptions?.map((mode, index) => (
                              <Fragment key={`${mode}_${index}`}>
                                <button
                                  className={ClassNames({
                                    selected:
                                      mode ===
                                      (modeLive ?? 'Ultra Fast'),
                                  })}
                                  style={{ padding: '4px 10px' }}
                                  key={mode}
                                  onClick={() => handleChangeMode(mode)}
                                >
                                  {`${mode}` ?? ''}
                                </button>
                                <br />
                              </Fragment>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </Fragment>
                  {!!video.fullscreen && (
                    <div className="control">
                      <button
                        onClick={handleControlsChange}
                        className="settings"
                      >
                        <img
                          src={
                            rotatable
                              ? video.controls
                                ? ControlIconOn
                                : ControlIconOff
                              : video.controls
                                ? MoveIconOn
                                : MoveIconOff
                          }
                          alt=""
                        />
                      </button>
                    </div>
                  )}
                  {!isPlaybackSmallMode && (
                    <div className="expand">
                      <button
                        onClick={() => handleFullscreen()}
                        className="fullscreen"
                      >
                        {!!video.fullscreen ? (
                          <img src={ExitFullscreenIcon} alt="" />
                        ) : (
                          <img src={FullscreenIcon} alt="" />
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="loading">
              <SpinnerComponent />
            </div>
          )
        ) : (
          !!video.src && (
            <Fragment>
              {video && video.controls && rotatable && (
                <Fragment>
                  <div className="controls-video">
                    <div className="move-buttons">
                      <div />
                      <button
                        onClick={() => handleRotate('up')}
                      >
                        <img
                          src={
                            rotate && rotate === 'up'
                              ? ArrowTopOnIcon
                              : ArrowTopOffIcon
                          }
                          alt="up_button"
                        />
                      </button>
                      <div />
                      <button
                        onClick={() => handleRotate('left')}
                      >
                        <img
                          src={
                            rotate && rotate === 'left'
                              ? ArrowLeftOnIcon
                              : ArrowLeftOffIcon
                          }
                          alt="left_button"
                        />
                      </button>
                      <button onClick={() => onReset()}>
                        <img src={CenterControlIcon} alt="" />
                      </button>
                      <button
                        onClick={() => handleRotate('right')}
                      >
                        <img
                          src={
                            rotate && rotate === 'right'
                              ? ArrowRightOnIcon
                              : ArrowRightOffIcon
                          }
                          alt="right_button"
                        />
                      </button>
                      <div />
                      <button
                        onClick={() => handleRotate('down')}
                      >
                        <img
                          src={
                            rotate && rotate === 'down'
                              ? ArrowBottomOnIcon
                              : ArrowBottomOffIcon
                          }
                          alt="down_button"
                        />
                      </button>
                      <div />
                    </div>
                  </div>
                  <div className="zoom-buttons">
                    <button onClick={() => handleZoom('zoom+')}>
                      <img
                        src={
                          zooming && zooming === 'zoom+'
                            ? ZoomInOnIcon
                            : ZoomInOffIcon
                        }
                        alt="zoom in"
                      />
                    </button>
                    <button onClick={() => handleZoom('zoom-')}>
                      <img
                        src={
                          zooming && zooming === 'zoom-'
                            ? ZoomOutOnIcon
                            : ZoomOutOffIcon
                        }
                        alt="zoom out"
                      />
                    </button>
                  </div>
                </Fragment>
              )}
              {type === 'streaming' && !isPlaybackSmallMode && (
                <div
                  className={
                    pin?.find((item) => item === serial) || isPinAll
                      ? 'top-player-component no-select pinCam'
                      : 'top-player-component no-select'
                  }
                >
                  <div className="stream_name">
                    <div className="status">{name}</div>
                    {pin?.find((item) => item === serial) || isPinAll ? (
                      <PushpinFilled
                        className="pin"
                        onClick={() => handleUnPin()}
                      />
                    ) : (
                      <PushpinOutlined
                        className="pin"
                        onClick={() => handlePin()}
                      />
                    )}
                  </div>
                  {status && status !== 'live' && (
                    <div className="stream_state">
                      <div className="status">{status}</div>
                    </div>
                  )}
                </div>
              )}
              <div className="bottom-player-component no-select">
                {
                  tabActive === 'live' && (
                    video.playing ? (
                      <div>
                        <button onClick={() => handlePause()} className="play">
                          <img src={PauseIcon} alt="" />
                        </button>
                      </div>
                    ) : video.ended ? (
                      <div>
                        <button onClick={() => handleRePlay()} className="play">
                          <img src={ReplayIcon} alt="" />
                        </button>
                      </div>
                    ) : (
                      <div>
                        <button onClick={() => handlePlay()} className="play">
                          <img src={PlayArrowIcon} alt="" />
                        </button>
                      </div>
                    )
                  )
                }
                {video.type === 'video' && tabActive === 'live' && (
                  <Fragment>
                    <div
                      onClick={handleSeekChange}
                      onMouseUp={handleSeekMouseUp}
                      onMouseDown={handleSeekMouseDown}
                      ref={progress}
                      className="progress"
                    >
                      <div
                        className="progress-filled"
                        style={{
                          flexBasis: `${video.played * 100}%`,
                        }}
                      />
                      <div
                        className="point"
                        style={{ marginLeft: `${video.played * 100}%` }}
                      >
                        <div className="timer">
                          <button className="timing">
                            <DurationComponent
                              seconds={
                                video &&
                                Math.round(video.played * video.duration)
                              }
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div>
                      <button className="time">
                        <DurationComponent seconds={video.duration} />
                      </button>
                    </div>

                    <div className="speed">
                      <button>
                        {
                          speeds.filter(
                            (speed) => speed.value === video.playbackRate
                          )[0].title
                        }
                      </button>
                      <div className="speeds">
                        {speeds.map((q, index) => (
                          <Fragment key={`${q?.value}_${index}`}>
                            <button
                              className={ClassNames({
                                selected: q.value === video.playbackRate,
                              })}
                              key={q.value}
                              onClick={() => handleSpeedChange(q.value)}
                            >
                              {q.title}
                            </button>
                            <br />
                          </Fragment>
                        ))}
                      </div>
                    </div>
                    {permission &&
                      permission.find((item) => item === 'download') && (
                        <div className="download">
                          <button>
                            <a
                              href={src}
                              download
                              target="_blank"
                              onClick={() =>
                                handleReportActionLog(logContent, logSerial)
                              }
                            >
                              <img src={DownloadIcon} alt="" width="35px" />
                            </a>
                          </button>
                        </div>
                      )}
                  </Fragment>
                )}
                {video.type === 'streaming' && (
                  <div className='bottom-player-control-left'>
                    <div>
                      {tabActive === 'live' && (
                        <>
                          <button className="stream">
                            <img src={StreamIcon} alt="" />
                          </button>
                          {!isPlaybackSmallMode && (
                            <div>
                              <button
                                onClick={() => {
                                  onResetStream({
                                    qualityLevel: zoomStatePlayer?.zoomState,
                                  })
                                }}
                                className="reset-stream"
                              >
                                <img src={ResetIcon} alt="" />
                              </button>
                            </div>
                          )}
                        </>
                      )}
                      <button className="time">
                        <DurationComponent
                          seconds={
                            refVideo.current.currentTime
                          }
                        />
                      </button>
                    </div>
                    {tabActive === 'live' && (<div className="stream-progress" />)}

                    <div>
                      {!isPlaybackSmallMode && (
                        <div className="download">
                          <button onClick={onDownloadDirectClick}>
                            <CameraOutlined style={{ fontSize: '20px' }} />
                          </button>
                        </div>
                      )}
                      {!isPlaybackSmallMode && tabActive === 'live' && (
                        <>
                          <div className="speed">
                            <button className="resolution">
                              {`${zoomStatePlayer?.resolution || 720}p` ?? ''}
                            </button>
                            <div className="speeds">
                              {zoomStateOption?.map((q, index) => (
                                <Fragment key={`${q?.zoomState}_${index}`}>
                                  <button
                                    className={ClassNames({
                                      selected:
                                        q?.zoomState ===
                                        zoomStatePlayer?.zoomState,
                                    })}
                                    key={q?.zoomState}
                                    onClick={() => {
                                      setZoomStatePlayer({
                                        dataZoomStates:
                                          zoomStatePlayer?.dataZoomStates
                                            ? [...zoomStatePlayer?.dataZoomStates]
                                            : [],
                                        resolution: q?.resolution,
                                        zoomState: q?.zoomState,
                                      })
                                      onResetQualityStream({
                                        qualityLevel: q?.zoomState,
                                      })
                                    }}
                                  >
                                    {`${q?.resolution || 720}p` ?? ''}
                                  </button>
                                  <br />
                                </Fragment>
                              ))}
                            </div>
                          </div>
                          <div className="speed">
                            <button
                              className="resolution"
                              style={{
                                width:
                                  modeLive === 'Fast' || '70px'
                              }}
                            >
                              {modeLive ?? 'Ultra Fast'}
                            </button>
                            <div className="speeds" style={{ width: '82px' }}>
                              {streamOptions?.map((mode, index) => (
                                <Fragment key={`${mode}_${index}`}>
                                  <button
                                    className={ClassNames({
                                      selected:
                                        mode ===
                                        (modeLive ?? 'Ultra Fast'),
                                    })}
                                    style={{ padding: '4px 10px' }}
                                    key={mode}
                                    onClick={() => handleChangeMode(mode)}
                                  >
                                    {`${mode}` ?? ''}
                                  </button>
                                  <br />
                                </Fragment>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
                <div className='bottom-player-control-right'>
                  {!!video.fullscreen && (
                    <div className="control">
                      <button onClick={handleControlsChange} className="settings">
                        <img
                          src={
                            rotatable
                              ? video.controls
                                ? ControlIconOn
                                : ControlIconOff
                              : video.controls
                                ? MoveIconOn
                                : MoveIconOff
                          }
                          alt=""
                        />
                      </button>
                    </div>
                  )}
                  <div className="expand">
                    <button
                      onClick={() => handleFullscreen()}
                      className="fullscreen"
                    >
                      {video.fullscreen ? (
                        <img src={ExitFullscreenIcon} alt="" />
                      ) : (
                        <img src={FullscreenIcon} alt="" />
                      )}
                    </button>
                  </div>
                </div>
                <canvas crossOrigin="anonymous" ref={canvasRef} hidden />
              </div>
            </Fragment>
          )
        )}
      </figure>
      {!!(dataUser.enableWatermark && video.playing) && (
        <div className="watermark-container">
          {[...Array(8)].map((item) => (
            <p
              key={item}
              style={{ transform: 'rotate(330deg)', opacity: '0.6' }}
            >
              {dataUser.phoneNumber}
            </p>
          ))}
        </div>
      )}
      {videoError && <div className="video-error">{videoError}</div>}
    </div>
  )
}

export default MilestonePlayerComponent
